// Copyright (c) StrangeBee 2024

import passwordValidator from 'password-validator';
import { PasswordPolicyOutput } from 'thehive-sdk';
import { UseLocalMessageType } from './locale';

const factoryValidator: Record<string, (...args: any[]) => any> = {
    minLength: (validator: passwordValidator, value: any): passwordValidator => validator.is().min(Math.max(value, 1)),
    minLowerCase: (validator: passwordValidator, value: any): passwordValidator => validator.has().lowercase(value),
    minUpperCase: (validator: passwordValidator, value: any): passwordValidator => validator.has().uppercase(value),
    minDigit: (validator: passwordValidator, value: any): passwordValidator => validator.has().digits(value),
    minSpecial: (validator: passwordValidator, value: any): passwordValidator => validator.has().symbols(value),
    cannotContainUsername: (validator: passwordValidator, value: any): passwordValidator => {
        const regex = new RegExp(`${value}`, 'g');
        return validator.has().not(regex);
    },
    default: (validator: passwordValidator, {}: any): passwordValidator => validator,
};

const factoryReadable: Record<string, (...args: any[]) => any> = {
    minLength: (local: UseLocalMessageType, value: any): string => local('password.rules.minLength', { value }),
    minLowerCase: (local: UseLocalMessageType, value: any): string => local('password.rules.minLowerCase', { value }),
    minUpperCase: (local: UseLocalMessageType, value: any): string => local('password.rules.minUpperCase', { value }),
    minDigit: (local: UseLocalMessageType, value: any): string => local('password.rules.minDigit', { value }),
    minSpecial: (local: UseLocalMessageType, value: any): string => local('password.rules.minSpecial', { value }),
    cannotContainUsername: (local: UseLocalMessageType, value: any): string =>
        local('password.rules.cannotContainUsername', { value }),
};

const mapError: Record<string, string> = {
    minLength: 'min',
    minLowerCase: 'lowercase',
    minUpperCase: 'uppercase',
    minDigit: 'digits',
    minSpecial: 'symbols',
    cannotContainUsername: 'not',
};

export const getReadableRulePassword = (rules: Record<string, any>, local: UseLocalMessageType): string[] => {
    return Object.entries(rules).reduce(
        (acc, [attr, value]): string[] =>
            factoryReadable[attr] && value && (parseInt(value) > 0 || typeof value === 'string')
                ? [...acc, factoryReadable[attr](local, value)]
                : acc,
        [] as string[],
    );
};

export interface ValidRulePassword {
    text: string;
    error: boolean;
}

export const getValidRulePassword = (
    rules: Record<string, any>,
    local: UseLocalMessageType,
    error: string[],
): ValidRulePassword[] => {
    return Object.entries(rules).reduce(
        (acc, [attr, value]): ValidRulePassword[] =>
            factoryReadable[attr] && value && (parseInt(value) > 0 || typeof value === 'string')
                ? [...acc, { text: factoryReadable[attr](local, value), error: error.includes(mapError[attr] || '') }]
                : acc,
        [] as ValidRulePassword[],
    );
};

export const computeValidatePassword = (rules: PasswordPolicyOutput): ((...args: any[]) => any) => {
    const schema = new passwordValidator();

    const computeValidator = Object.entries(rules).reduce(
        (acc, [attr, value]): passwordValidator =>
            factoryReadable[attr] && value && (parseInt(value) > 0 || typeof value === 'string')
                ? factoryValidator[attr](acc, value)
                : acc,
        schema,
    );

    return function (password: string): boolean | string[] {
        return !rules.enabled || computeValidator.validate(password, { list: true });
    };
};
